import { observer } from "mobx-react-lite";
import { useSnackbar } from "src/notifications/SnackbarProvider.ts";
import { AlertV2 } from "src/components/AlertV2/AlertV2.tsx";
import { css } from "@emotion/react";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { AnimatePresence, motion } from "framer-motion";

export const Snackbar = observer(function SnackbarObserved() {
  const snackbarStore = useSnackbar();
  const flashMessages = snackbarStore.snacks.filter((snack) => snack.flash);
  const bottomRightMessages = snackbarStore.snacks.filter(
    (snack) =>
      !snack.flash &&
      snack.anchorOrigin.horizontal === "right" &&
      snack.anchorOrigin.vertical === "bottom",
  );
  const topRightMessages = snackbarStore.snacks.filter(
    (snack) =>
      !snack.flash &&
      snack.anchorOrigin.horizontal === "right" &&
      snack.anchorOrigin.vertical === "top",
  );

  const topCenterMessages = snackbarStore.snacks.filter(
    (snack) =>
      !snack.flash &&
      snack.anchorOrigin.horizontal === "center" &&
      snack.anchorOrigin.vertical === "top",
  );

  return (
    <>
      <div css={containerTopCenter}>
        {flashMessages.map((snack) => (
          <AlertV2
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            progress={
              snack.showProgress ? snackbarStore.progress[snack.id] : undefined
            }
            onPointerEnter={() => {
              snackbarStore?.pauseProgress(snack.id);
            }}
            onPointerLeave={() => {
              snackbarStore?.unpauseProgress(snack.id);
            }}
            transition={{
              duration: 0.5,
              type: "spring",
            }}
            style={{
              maxWidth: 400,
            }}
            key={snack.id}
            severity={snack.type}
            showCloseButton
            onClose={() => {
              snackbarStore.close(snack.id);
            }}
          >
            {snack.message}
          </AlertV2>
        ))}
      </div>

      <motion.div css={containerBottomRight}>
        {bottomRightMessages.map((snack) => (
          <AlertV2
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            style={{
              width: 300,
            }}
            onPointerEnter={() => {
              snackbarStore?.pauseProgress(snack.id);
            }}
            onPointerLeave={() => {
              snackbarStore?.unpauseProgress(snack.id);
            }}
            progress={
              snack.showProgress ? snackbarStore.progress[snack.id] : undefined
            }
            key={snack.id}
            severity={snack.type}
            showCloseButton
            onClose={() => {
              snackbarStore.close(snack.id);
            }}
          >
            {snack.message}
          </AlertV2>
        ))}
      </motion.div>

      <motion.div css={containerTopCenter}>
        {topCenterMessages.map((snack) => (
          <AlertV2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            progress={
              snack.showProgress ? snackbarStore.progress[snack.id] : undefined
            }
            onPointerEnter={() => {
              snackbarStore?.pauseProgress(snack.id);
            }}
            onPointerLeave={() => {
              snackbarStore?.unpauseProgress(snack.id);
            }}
            style={{
              minWidth: 300,
            }}
            key={snack.id}
            severity={snack.type}
            showCloseButton
            onClose={() => {
              snackbarStore.close(snack.id);
            }}
          >
            {snack.message}
          </AlertV2>
        ))}
      </motion.div>

      <div css={containerTopRight}>
        <AnimatePresence>
          {topRightMessages.map((snack) => (
            <AlertV2
              layout
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              progress={
                snack.showProgress
                  ? snackbarStore.progress[snack.id]
                  : undefined
              }
              onPointerEnter={() => {
                snackbarStore?.pauseProgress(snack.id);
              }}
              onPointerLeave={() => {
                snackbarStore?.unpauseProgress(snack.id);
              }}
              style={{
                width: 300,
              }}
              key={snack.id}
              severity={snack.type}
              showCloseButton
              onClose={() => {
                snackbarStore.close(snack.id);
              }}
            >
              {snack.message}
            </AlertV2>
          ))}
        </AnimatePresence>
      </div>
    </>
  );
});

const containerTopCenter = (theme: ExactTheme) => css`
  position: absolute;
  top: 15px;
  left: 50%;
  transform-origin: center;
  transform: translateX(-50%);
  z-index: ${theme.zIndices.notifications};
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const containerBottomRight = (theme: ExactTheme) => css`
  position: absolute;
  bottom: 15px;
  right: 15px;

  z-index: ${theme.zIndices.notifications};
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const containerTopRight = (theme: ExactTheme) => css`
  position: absolute;
  top: 15px;
  right: 15px;

  z-index: ${theme.zIndices.notifications};
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
